import { v4 as uuidv4 } from "uuid";
import { z } from "zod/v4";

import * as y from "../schemas";

const markerTypes = ["controller", "egw", "multiSensor", "smartVue"] as const;

/**
 * Marker device type.
 */
export type MarkerType = (typeof markerTypes)[number];

const rawSchema = z.looseObject({
    markerPositionID: y.id().nullish(),
    floorSpace_ID: y.id(),
    markerType: z.enum(markerTypes),
    identifier: z.string().nullish(),
    notes: z.string().nullish(),
    x: z.number(),
    y: z.number(),
    z: z.number(),
    angle: z.number().default(0),
});

const _strict = rawSchema.strict();

/**
 * Raw marker position as returned by the API.
 */
export type RawMarkerPosition = z.infer<typeof _strict>;

/**
 * Marker position.
 */
export const schema = y.restorable(rawSchema, (marker) => ({
    uniqueId: uuidv4(),
    changed: false,
    id: marker.markerPositionID,
    floorSpaceId: marker.floorSpace_ID,
    markerType: marker.markerType,
    identifier: marker.identifier,
    notes: marker.notes,
    x: marker.x,
    y: marker.y,
    z: marker.z,
    angle: marker.angle,
}));

/**
 * Marker position.
 */
export type MarkerPosition = z.infer<typeof schema>;

/**
 * Maps a MarkerPosition to a RawMarkerPosition.
 *
 * @param {MarkerPosition} marker - The marker position to map.
 * @returns {RawMarkerPosition} The raw marker position object.
 */
export const toRaw = (marker: MarkerPosition): RawMarkerPosition => ({
    ...marker.__original,
    markerPositionID: marker.id,
    floorSpace_ID: marker.floorSpaceId,
    markerType: marker.markerType,
    identifier: marker.identifier,
    notes: marker.notes,
    x: marker.x,
    y: marker.y,
    z: marker.z,
    angle: marker.angle,
});
